import { useDispatch, useSelector } from 'react-redux';
import Axios from '@/shared/api/axios';
import { getIdolArray } from '@/shared/store/slices/idolSlice';
import { setCurrentIdol } from '@/shared/store/slices/userSlice';

/**
 * 偶像是否可刪除：停權偶像不可刪；未停權偶像須在未停權偶像數大於 1 時才可刪，
 * 避免刪到沒有可切換的偶像。
 *
 * @param {Object} idol - 要刪除的偶像（/idol/get-idol-array 的一筆）
 * @param {Array} idolList - 目前的偶像列表
 */
export const canDeleteIdol = (idol, idolList) =>
  !idol.idol_is_suspended && idolList.filter((i) => !i.idol_is_suspended).length > 1;

/**
 * 刪除偶像：delete-idol → 重抓偶像列表 → 若刪的是目前偶像，切換到第一個未停權的偶像。
 * 用於「我的偶像」與偶像編輯頁。
 *
 * 回傳 status_code：全部成功為 SYSTEM_1000；任一步失敗時回傳該步的狀態碼
 * （列表請求被 reject 時為攔截器附加的 httpErrorCode，含 NETWORK_ERROR），由呼叫端交給 StatusModal。
 * 請求的例外（HTTP 錯誤、逾時）直接拋出，由呼叫端的標準 catch 處理。
 */
export function useDeleteIdol() {
  const dispatch = useDispatch();
  const currentIdolId = useSelector((state) => state.user.idolId);

  return async (idolId) => {
    const res = await Axios.post('/idol/delete-idol', { idol_id: idolId });
    if (res.data.status_code !== 'SYSTEM_1000') {
      return res.data.status_code;
    }

    const idolArrayResult = await dispatch(getIdolArray());
    if (getIdolArray.rejected.match(idolArrayResult)) {
      return idolArrayResult.payload;
    }
    // 與 idolSlice fulfilled 的判斷一致：data 不是陣列即視為失敗
    if (!Array.isArray(idolArrayResult.payload.data)) {
      return idolArrayResult.payload.status_code;
    }
    if (idolId !== currentIdolId) {
      return 'SYSTEM_1000';
    }

    const nextIdol = idolArrayResult.payload.data.find((i) => !i.idol_is_suspended);
    if (!nextIdol) {
      // canDeleteIdol 以畫面上的列表判斷，正常情況刪除後仍有未停權偶像；找不到代表按下刪除前列表已過期
      // （例如其他偶像剛被停權）。此時目前偶像已刪除，屬 §1「目前偶像已被刪除」不處理範圍，不做容錯，讓問題暴露（由呼叫端 catch 記 log）
      throw new Error(`No available idol to switch to after deleting current idol ${idolId}`);
    }

    const switchRes = await Axios.post('/idol/switch-check', { idol_id: nextIdol.idol_id });
    if (switchRes.data.status_code !== 'SYSTEM_1000') {
      return switchRes.data.status_code;
    }

    dispatch(setCurrentIdol(nextIdol));
    return 'SYSTEM_1000';
  };
}
